import React from 'react';
import { Send, Clock, CheckCircle, AlertCircle, Download, Loader2 } from 'lucide-react';

/** Purpose: Renders status timeline and workflow steps. Inputs: workflow props. Output: React render output (JSX). */
export default function WorkflowSection({
  request,
  timelineStages,
  currentTimelineIndex,
  nextAction,
  justCreated,
  authFlowStatus,
  authFlowStep,
  authFlowError,
  authFlowAuthorizationId,
  isUpdating,
  activeAuthorizations,
  onOpenCreateAuth,
  onResumeAuthorization,
  onRestartAuthorization,
  onAttachExistingAuthorization,
  canComposeEmail,
  isManualDelivery,
  hasPortalLink,
  onDownloadPackage,
  onOpenSendPreview,
  sendError,
  portalLoading,
  onGeneratePortalLink,
  canGeneratePortalLink,
  portalError,
  portalLink,
  portalPin,
  portalCopied,
  onCopy,
  needsFollowUp,
  daysSinceSent,
  onLogFollowUp,
  noteSaving,
  showStatusUpdate,
  setShowStatusUpdate,
  statusError,
  onClearStatusError,
  onStatusUpdate,
  denialReason,
  setDenialReason,
  isDev,
  showDevTestSend,
  setShowDevTestSend,
  testEmail,
  setTestEmail,
  testSendError,
  setTestSendError,
  testSendSuccess,
  setTestSendSuccess,
  testSending,
  onTestSend,
  buildEmailDraft,
  setEmailTo,
  setEmailSubject,
  setEmailMessage,
  setSendError,
  setGmailDraftError
}) {
  return (
    <>
      <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
        <h4 className="font-medium text-slate-900 mb-3">Status Timeline</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {timelineStages.map((label, index) => {
            const complete = index < currentTimelineIndex;
            const active = index === currentTimelineIndex;
            return (
              <div
                key={label}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  active
                    ? 'border-purple-500 bg-purple-50'
                    : complete
                      ? 'border-green-300 bg-green-50'
                      : 'border-slate-200 bg-white'
                }`}
              >
                <p className={`font-medium ${active ? 'text-purple-700' : complete ? 'text-green-700' : 'text-slate-600'}`}>
                  {label}
                </p>
              </div>
            );
          })}
        </div>
        <div className="mt-3 p-3 rounded-lg bg-white border border-slate-200 text-sm">
          <p className="text-slate-500">Recommended Next Action</p>
          <p className="text-slate-900 font-medium mt-1">{nextAction}</p>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h4 className="font-medium text-slate-900 mb-4">Workflow</h4>

        {justCreated && (
          <div className="mb-4 p-4 border border-blue-200 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              Request created - here is what to do next.
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Complete the steps below to send the request and enable secure uploads.
            </p>
          </div>
        )}

        <div className={`p-4 rounded-lg mb-3 ${request.authorizationId ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {request.authorizationId ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600" />
              )}
              <div>
                <p className="font-medium text-slate-900">1. HIPAA Authorization</p>
                <p className="text-sm text-slate-600">
                  {request.authorizationId
                    ? 'Authorization signed and attached'
                    : 'Sign a HIPAA authorization to release your records'}
                </p>
                {authFlowStatus === 'running' && (
                  <p className="text-xs text-blue-700 mt-2">Attaching authorization… Step {authFlowStep} of 3</p>
                )}
                {authFlowStatus === 'error' && authFlowError && (
                  <p className="text-xs text-red-600 mt-2">{authFlowError}</p>
                )}
              </div>
            </div>
            {!request.authorizationId && (
              <div className="flex gap-2">
                <button
                  onClick={onOpenCreateAuth}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
                >
                  Sign Authorization
                </button>
                {authFlowAuthorizationId && (
                  <>
                    <button
                      onClick={onResumeAuthorization}
                      disabled={isUpdating}
                      className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
                    >
                      Resume Attach
                    </button>
                    <button
                      onClick={onRestartAuthorization}
                      disabled={isUpdating}
                      className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
                    >
                      Restart
                    </button>
                  </>
                )}
                {activeAuthorizations.length > 0 && (
                  <select
                    onChange={(e) => e.target.value && onAttachExistingAuthorization(e.target.value)}
                    disabled={isUpdating}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  >
                    <option value="">Use existing...</option>
                    {activeAuthorizations.map(auth => (
                      <option key={auth.id} value={auth.id}>
                        {auth.grantedTo} - {auth.organization}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={`p-4 rounded-lg mb-3 ${request.authorizationId ? (hasPortalLink ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200') : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              {hasPortalLink ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <Clock className={`w-5 h-5 ${request.authorizationId ? 'text-blue-600' : 'text-slate-400'}`} />
              )}
              <div>
                <p className="font-medium text-slate-900">2. Provider Upload Portal</p>
                <p className="text-sm text-slate-600">
                  {canGeneratePortalLink
                    ? 'Share a secure upload link so the provider can send records directly.'
                    : 'Attach a signed authorization to enable the secure upload portal.'}
                </p>
              </div>
            </div>
            <div>
              <button
                onClick={onGeneratePortalLink}
                disabled={!canGeneratePortalLink || portalLoading}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {portalLoading ? 'Generating…' : 'Generate Link'}
              </button>
            </div>
          </div>

          {portalError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {portalError}
            </div>
          )}

          {portalLink && (
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <p className="text-slate-600 mb-1">Upload Link</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={portalLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-xs"
                  />
                  <button
                    onClick={() => onCopy('Link', portalLink)}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-xs hover:bg-slate-50"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
              <div>
                <p className="text-slate-600 mb-1">PIN</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={portalPin || 'Not set'}
                    readOnly
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-xs"
                  />
                  {portalPin && (
                    <button
                      onClick={() => onCopy('PIN', portalPin)}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-xs hover:bg-slate-50"
                    >
                      Copy PIN
                    </button>
                  )}
                </div>
              </div>
              {portalCopied && (
                <p className="text-xs text-emerald-600">{portalCopied}</p>
              )}
            </div>
          )}
        </div>

        <div className={`p-4 rounded-lg mb-3 ${request.sentAt ? 'bg-green-50 border border-green-200' : request.authorizationId ? (hasPortalLink ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200') : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {request.sentAt ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : request.authorizationId ? (
                <Send className={`w-5 h-5 ${hasPortalLink ? 'text-blue-600' : 'text-amber-600'}`} />
              ) : (
                <Clock className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="font-medium text-slate-900">3. Send Request</p>
                <p className="text-sm text-slate-600">
                  {request.sentAt
                    ? `Sent on ${new Date(request.sentAt).toLocaleDateString()}`
                    : canComposeEmail
                      ? `Compose and send this request email to ${request.email}`
                      : 'Download package and send to provider'}
                </p>
                {!request.sentAt && (
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      isManualDelivery ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {isManualDelivery ? 'Manual send required' : 'Email send available'}
                    </span>
                  </div>
                )}
                {!request.sentAt && !hasPortalLink && request.authorizationId && (
                  <p className="text-xs text-amber-700 mt-2">
                    Generate the portal upload link first, then send the request email.
                  </p>
                )}
                {!request.sentAt && canComposeEmail && request.deliveryMethod !== 'EMAIL' && (
                  <p className="text-xs text-slate-500 mt-2">
                    Delivery preference is {request.deliveryMethod.toLowerCase()}, but you can still send the request package by email from this screen.
                  </p>
                )}
                {!request.sentAt && isManualDelivery && (
                  <p className="text-xs text-slate-500 mt-2">
                    No provider email on file. Download and send the package manually, then confirm it was sent.
                  </p>
                )}
                {!request.sentAt && request.deliveryMethod === 'EMAIL' && !request.email && (
                  <p className="text-xs text-amber-600">
                    No provider email on file. Add one to send automatically, or continue with manual send.
                  </p>
                )}
              </div>
            </div>
            {request.authorizationId && !request.sentAt && (
              <div className="flex gap-2">
                <button
                  onClick={onDownloadPackage}
                  className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Package
                </button>
                <button
                  onClick={() => {
                    const draft = buildEmailDraft();
                    setEmailTo(draft.to);
                    setEmailSubject(draft.subject);
                    setEmailMessage(draft.message);
                    setSendError('');
                    if (typeof setGmailDraftError === 'function') setGmailDraftError('');
                    onOpenSendPreview();
                  }}
                  disabled={isUpdating || !hasPortalLink}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  Preview & Send
                </button>
              </div>
            )}
          </div>
          {sendError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {sendError}
            </div>
          )}
        </div>

        {needsFollowUp && (
          <div className="p-4 rounded-lg mb-3 bg-amber-50 border border-amber-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Follow-up reminder</p>
                  <p className="text-sm text-slate-700">
                    This request was sent {daysSinceSent} days ago and has no response status yet.
                  </p>
                </div>
              </div>
              <button
                onClick={onLogFollowUp}
                disabled={noteSaving}
                className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
              >
                {noteSaving ? 'Logging...' : 'Log Follow-up'}
              </button>
            </div>
          </div>
        )}

        {request.sentAt && (
          <div className={`p-4 rounded-lg ${['RECEIVED', 'PARTIALLY_RECEIVED'].includes(request.status) ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
            {(() => {
              const portalEvents = (request.communications || []).filter(c => c.method === 'portal' || c.createdBy === 'system');
              const portalUploads = portalEvents.filter(c => c.type === 'received');
              const portalAccess = portalEvents.filter(c => c.type === 'status_change' && (c.content || '').includes('accessed'));
              return (portalUploads.length > 0 || portalAccess.length > 0) ? (
                <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
                  <p className="font-medium text-emerald-800">Portal Activity Detected</p>
                  {portalAccess.length > 0 && (
                    <p className="text-emerald-700 mt-1">Provider accessed the portal on {new Date(portalAccess[0].createdAt).toLocaleDateString()}</p>
                  )}
                  {portalUploads.length > 0 && (
                    <p className="text-emerald-700 mt-1">{portalUploads.length} record{portalUploads.length > 1 ? 's' : ''} uploaded via portal</p>
                  )}
                </div>
              ) : null;
            })()}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {['RECEIVED', 'PARTIALLY_RECEIVED'].includes(request.status) ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-900">4. Track Response</p>
                  <p className="text-sm text-slate-600">
                    Update status as you hear from the provider
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
              >
                Update Status
              </button>
            </div>

            {showStatusUpdate && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                {statusError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                    {statusError}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => onStatusUpdate('ACKNOWLEDGED')}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-purple-50 hover:border-purple-500"
                  >
                    Acknowledged
                  </button>
                  <button
                    onClick={() => onStatusUpdate('IN_PROGRESS')}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-cyan-50 hover:border-cyan-500"
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => onStatusUpdate('PARTIALLY_RECEIVED')}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-500"
                  >
                    Partial Records
                  </button>
                  <button
                    onClick={() => onStatusUpdate('RECEIVED')}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-green-50 hover:border-green-500"
                  >
                    ✓ All Received
                  </button>
                  <button
                    onClick={() => onStatusUpdate('DENIED', denialReason.trim())}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-red-50 hover:border-red-500"
                  >
                    Denied
                  </button>
                  <button
                    onClick={() => onStatusUpdate('CANCELLED')}
                    className="p-2 text-sm border border-slate-200 rounded-lg hover:bg-gray-50 hover:border-gray-500"
                  >
                    Cancel
                  </button>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Denial reason (required if denied)
                  </label>
                  <input
                    type="text"
                    value={denialReason}
                    onChange={(e) => {
                      setDenialReason(e.target.value);
                      if (statusError) onClearStatusError();
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="e.g., Missing signature, fee required..."
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {isDev && request.authorizationId && !request.sentAt && (
          <div className="p-4 rounded-lg bg-slate-50 border border-dashed border-slate-300">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Developer Test Tools</p>
                <p className="text-xs text-slate-600">Use test send without mixing it into live workflow actions.</p>
              </div>
              <button
                onClick={() => setShowDevTestSend(prev => !prev)}
                className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100"
              >
                {showDevTestSend ? 'Hide Test Send' : 'Show Test Send'}
              </button>
            </div>
            {showDevTestSend && (
              <div className="mt-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => {
                      setTestEmail(e.target.value);
                      if (testSendError) setTestSendError('');
                      if (testSendSuccess) setTestSendSuccess('');
                    }}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="test@example.com"
                  />
                  <button
                    onClick={onTestSend}
                    disabled={testSending}
                    className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                  >
                    {testSending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send Test Email
                  </button>
                </div>
                {testSendError && (
                  <p className="text-xs text-red-600 mt-2">{testSendError}</p>
                )}
                {testSendSuccess && (
                  <p className="text-xs text-green-600 mt-2">{testSendSuccess}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
